import React from 'react'
import { Dropdown, SelectDropdownItem } from '@sketch/components'
import { actionFilterItems, ActionFilterItem } from './actionFilterOptions'
import {
  ActionFilterWrapper,
  ActionSelectDropdown,
} from './WorkspaceSettingsAuditLog.styles'
import type { WorkspaceAuditAction } from './types'

type ActionItem = ExtractSafe<
  ActionFilterItem,
  ActionFilterItem & { kind: 'action' }
>

export interface WorkspaceAuditLogActionFilterProps {
  value?: WorkspaceAuditAction[]
  onChange: (value: WorkspaceAuditAction[] | undefined) => void
}

const ALL_ACTIONS_LABEL = 'All actions'

const isActionItem = (item: ActionFilterItem | undefined): item is ActionItem =>
  item?.kind === 'action'

const getActionFilterItemKey = (item: ActionFilterItem) =>
  item.kind === 'all' ? item.kind : item.value

const getSelectedActionFilterLabel = (
  selectedActions: WorkspaceAuditAction[]
): string => {
  if (selectedActions.length === 0) return ALL_ACTIONS_LABEL

  if (selectedActions.length === 1) {
    const selectedItem = actionFilterItems.find(
      (item): item is ActionItem =>
        isActionItem(item) && selectedActions.includes(item.value)
    )

    return selectedItem?.buttonLabel ?? ALL_ACTIONS_LABEL
  }

  return `${selectedActions.length} actions selected`
}

const toggleSelectedAction = (
  selectedActions: WorkspaceAuditAction[],
  action: WorkspaceAuditAction
) => {
  if (selectedActions.includes(action)) {
    return selectedActions.filter(selectedAction => selectedAction !== action)
  }

  return [...selectedActions, action]
}

const isItemSelected = (
  item: ActionFilterItem,
  selectedActions: WorkspaceAuditAction[]
) =>
  item.kind === 'all'
    ? selectedActions.length === 0
    : selectedActions.includes(item.value)

const shouldRenderGroupHeader = (item: ActionFilterItem, itemIndex: number) => {
  if (!isActionItem(item)) return false

  const previousItem = actionFilterItems[itemIndex - 1]

  return !isActionItem(previousItem) || previousItem.group !== item.group
}

export const WorkspaceAuditLogActionFilter: React.FC<
  WorkspaceAuditLogActionFilterProps
> = ({ value, onChange }) => {
  const selectedActions = value ?? []

  const handleActionFilterChange = (item: ActionFilterItem) => {
    if (item.kind === 'all') {
      onChange(undefined)
      return
    }

    const nextSelectedActions = toggleSelectedAction(
      selectedActions,
      item.value
    )
    onChange(nextSelectedActions.length > 0 ? nextSelectedActions : undefined)
  }

  return (
    <ActionFilterWrapper>
      <ActionSelectDropdown
        placeholder={getSelectedActionFilterLabel(selectedActions)}
        items={actionFilterItems}
        onChange={handleActionFilterChange}
        renderValueText={item => item.label}
        renderItem={(item, { setItem, a11yProps }) => {
          const itemIndex = actionFilterItems.indexOf(item)
          const shouldShowGroupHeader = shouldRenderGroupHeader(item, itemIndex)
          const isActive = isItemSelected(item, selectedActions)
          const groupHeader =
            shouldShowGroupHeader && isActionItem(item) ? (
              <>
                <Dropdown.Divider />
                <Dropdown.Header>{item.group}</Dropdown.Header>
              </>
            ) : null

          return (
            <React.Fragment key={getActionFilterItemKey(item)}>
              {groupHeader}
              <SelectDropdownItem
                active={isActive}
                text={item.label}
                onClick={() => setItem(item)}
                {...a11yProps}
                aria-selected={isActive}
              />
            </React.Fragment>
          )
        }}
      />
    </ActionFilterWrapper>
  )
}
